import { useEffect, useState, useSyncExternalStore } from "react";

let deferredPrompt = null;
const subs = new Set();
const emit = () => subs.forEach((f) => f());

export const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
export const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
export const isIOSSafari = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream && !isStandalone();
export const isInstalled = () => isStandalone() || !!localStorage.getItem("dropit-installed");

if (typeof window !== "undefined") {
  if (isStandalone()) localStorage.setItem("dropit-installed", "1");
  window.matchMedia?.("(display-mode: standalone)").addEventListener?.("change", (e) => { if (e.matches) { localStorage.setItem("dropit-installed", "1"); emit(); } });
  // Chrome may fire beforeinstallprompt for an already-installed app (e.g. manifest id changed); never surface it then.
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); if (isInstalled()) return; deferredPrompt = e; emit(); });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; localStorage.setItem("dropit-installed", "1"); emit(); });
}

export const useInstallPrompt = () => {
  const p = useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => deferredPrompt, () => null);
  const install = async () => { if (!p) return false; p.prompt(); const { outcome } = await p.userChoice; if (outcome === "accepted") { deferredPrompt = null; localStorage.setItem("dropit-installed", "1"); emit(); } return outcome === "accepted"; };
  return { canInstall: !!p && !isInstalled(), install };
};

// --- SW update lifecycle: new worker installed while a controller exists → persistent banner; user taps → SKIP_WAITING → one reload.
let waitingWorker = null;
const updSubs = new Set();
const emitUpd = () => updSubs.forEach((f) => f());
export const useSwUpdate = () => {
  const w = useSyncExternalStore((f) => { updSubs.add(f); return () => updSubs.delete(f); }, () => waitingWorker, () => null);
  return { updateReady: !!w, applyUpdate: () => { sessionStorage.setItem("dropit-sw-reloading", "1"); w?.postMessage({ type: "SKIP_WAITING" }); } };
};

export const registerSW = () => {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", async () => {
    try {
      const reg = await navigator.serviceWorker.register("/sw.js");
      const onWaiting = (sw) => { waitingWorker = sw; emitUpd(); };
      if (reg.waiting && navigator.serviceWorker.controller) onWaiting(reg.waiting);
      reg.addEventListener("updatefound", () => { const nw = reg.installing; nw?.addEventListener("statechange", () => { if (nw.state === "installed" && navigator.serviceWorker.controller) onWaiting(nw); }); });
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing || !sessionStorage.getItem("dropit-sw-reloading")) return; // only reload when the user asked for the update
        refreshing = true; sessionStorage.removeItem("dropit-sw-reloading"); window.location.reload();
      });
      // Check for updates when the app regains focus, at most every 15 min.
      let lastCheck = Date.now();
      const check = () => { if (document.visibilityState === "visible" && navigator.onLine && Date.now() - lastCheck > 15 * 60000) { lastCheck = Date.now(); reg.update().catch(() => null); } };
      document.addEventListener("visibilitychange", check); window.addEventListener("focus", check);
      navigator.serviceWorker.addEventListener("message", (e) => { if (e.data?.type === "NAVIGATE" && e.data.url) window.location.assign(e.data.url); });
    } catch (e) { console.warn("SW registration failed", e); }
  });
};

export const swMessage = (msg) => new Promise((resolve) => {
  if (!navigator.serviceWorker?.controller) return resolve(null);
  const onMsg = (e) => { if (e.data?.type === (msg.type === "CLEAR_TILES" ? "TILES_CLEARED" : msg.type)) { navigator.serviceWorker.removeEventListener("message", onMsg); resolve(e.data); } };
  navigator.serviceWorker.addEventListener("message", onMsg);
  navigator.serviceWorker.controller.postMessage(msg);
  setTimeout(() => resolve(null), 3000);
});

export const useTileStats = (dep) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      const s = await swMessage({ type: "TILE_STATS" });
      let total = null; try { const est = await navigator.storage?.estimate?.(); total = est?.usage ?? null; } catch { /* none */ }
      setStats({ count: s?.count ?? 0, approxBytes: s?.approxBytes ?? 0, total });
    })();
  }, [dep]);
  return stats;
};
